import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  Users,
  Camera,
  Star,
  ArrowRight,
  Calendar,
  Euro,
  Castle,
  Utensils,
  Church,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useBookingModal } from "../../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const BrugesPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const tourHighlights = [
    {
      icon: Castle,
      title: t("1520", "Medieval Architecture"),
      description: t(
        "1521",
        "Explore stunning medieval buildings and cobblestone streets"
      ),
    },
    {
      icon: Church,
      title: t("1522", "Historic Churches"),
      description: t(
        "1523",
        "Visit beautiful churches and religious monuments"
      ),
    },
    {
      icon: Utensils,
      title: t("1524", "Belgian Cuisine"),
      description: t("1525", "Taste authentic Belgian chocolates and waffles"),
    },
    {
      icon: Camera,
      title: t("1482", "Photography"),
      description: t(
        "1526",
        "Capture the fairy-tale beauty of this UNESCO World Heritage site"
      ),
    },
  ];

  const tourFeatures = [
    t("1484", "Transportation service available"),
    t("1527", "Historic city exploration"),
    t("1528", "Market square visits"),
    t("1529", "Canal area viewing"),
    t("1530", "Cultural site access"),
    t("1489", "Photography opportunities"),
  ];

  const tourDetails = [
    {
      label: t("1490", "Duration"),
      value: t("1531", "Full Day (9-10 hours)"),
      icon: Clock,
    },
    {
      label: t("1492", "Group Size"),
      value: t("1493", "2-8 People"),
      icon: Users,
    },
    {
      label: t("1494", "Starting Point"),
      value: t("1495", "Schiphol Airport"),
      icon: MapPin,
    },
    {
      label: t("1496", "Price From"),
      value: t("1532", "€220 per person"),
      icon: Euro,
    },
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/tours/Bruges.webp"
            alt={t("1533", "Bruges Medieval City")}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <Castle className="h-4 w-4" />
              <span>{t("1534", "UNESCO World Heritage")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1535", "Medieval")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1536", "Bruges")}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-8">
              {t(
                "1537",
                "Step into a fairy tale in Belgium's most romantic medieval city"
              )}
            </p>

            <div className="flex items-center justify-center space-x-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-warning fill-current" />
              ))}
              <span className="text-white ml-2 text-lg">
                {t("1538", "4.8/5 (89 reviews)")}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  {t("1539", "A Living Medieval Museum")}
                </h2>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1540",
                    "Discover the enchanting city of Bruges, where medieval architecture meets modern charm. Walk through cobblestone streets, admire Gothic buildings, and experience the romance of this UNESCO World Heritage site."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1541",
                    "Our expert guides will take you through the historic city center, sharing fascinating stories about Bruges' golden age as a major European trading hub. Visit the famous Market Square, climb the Belfry Tower, and cruise along the peaceful canals."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed">
                  {t(
                    "1542",
                    "Don't miss the opportunity to taste world-famous Belgian chocolates, sample local beers, and enjoy traditional Belgian cuisine in authentic medieval settings."
                  )}
                </p>
              </div>

              {/* Tour Highlights */}
              <div className="grid grid-cols-2 gap-4">
                {tourHighlights.map((highlight) => (
                  <motion.div
                    key={highlight.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl border border-gray-700"
                  >
                    <highlight.icon className="h-8 w-8 text-primary mb-3" />
                    <div className="font-semibold text-white text-sm mb-2">
                      {highlight.title}
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {highlight.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl"
                >
                  <img
                    src="/images/tours/Bruges.webp"
                    alt={t("1543", "Bruges Market Square")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl mt-8"
                >
                  <img
                    src="/images/tours/Bruges.webp"
                    alt={t("1520", "Medieval Architecture")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl col-span-2"
                >
                  <img
                    src="/images/tours/Bruges.webp"
                    alt={t("1544", "Canal Cruise")}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tour Details */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1512", "Tour Details & Information")}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t(
                "1545",
                "Everything you need to know about your Bruges adventure"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Tour Details */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1546", "Quick Details")}
                </h3>

                <div className="space-y-6">
                  {tourDetails.map((detail) => (
                    <motion.div
                      key={detail.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-center space-x-4 p-4 bg-gray-700/30 rounded-xl"
                    >
                      <div className="w-12 h-12 bg-linear-to-r from-primary to-primary-light rounded-xl flex items-center justify-center">
                        <detail.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">
                          {detail.label}
                        </p>
                        <p className="text-gray-300">{detail.value}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Tour Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1515", "Tour Highlights")}
                </h3>

                <div className="space-y-4">
                  {tourFeatures.map((item) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <span className="text-gray-200">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative">
              <Castle className="h-16 w-16 mx-auto mb-6 opacity-80" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("1547", "Ready to Explore Medieval Bruges?")}
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                {t(
                  "1548",
                  "Book your magical journey to one of Europe's best-preserved medieval cities. Experience history, culture, and cuisine in this UNESCO World Heritage site."
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center space-x-2"
                >
                  <Calendar className="h-5 w-5" />
                  <span>{t("1549", "Book This Tour")}</span>
                </motion.button>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center space-x-2"
                  >
                    <span>{t("1519", "Ask Questions")}</span>
                    <ArrowRight className="h-5 w-5" />
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
